import type { Movie, MovieType } from "@/types/movie";

export type SortKey = "latest" | "views" | "rating";

export interface MovieQuery {
  type?: MovieType;
  region?: string;
  year?: string;
  genre?: string;
  keyword?: string;
  sort?: SortKey;
}

export const MOVIE_REGIONS = [
  "全部",
  "中国大陆",
  "中国香港",
  "美国",
  "英国",
  "日本",
  "韩国",
];

export const MOVIE_YEARS = ["全部", "2024", "2023", "2022", "2021"];

export const MOVIE_GENRES = [
  "全部",
  "剧情",
  "悬疑",
  "科幻",
  "动作",
  "犯罪",
  "冒险",
  "奇幻",
  "喜剧",
  "爱情",
  "古装",
  "历史",
  "战争",
  "都市",
  "青春",
  "美食",
  "刑侦",
  "医疗",
  "武侠",
  "恐怖",
  "治愈",
  "权谋",
  "史诗",
  "家庭",
  "文艺",
  "生存",
  "赛车",
  "惊悚",
];

export function formatViews(views: number): string {
  if (views >= 100000000) return `${(views / 100000000).toFixed(1)}亿`;
  if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
  return `${views}`;
}

export function filterMovies(list: Movie[], query: MovieQuery): Movie[] {
  const {
    type,
    region = "全部",
    year = "全部",
    genre = "全部",
    keyword = "",
    sort = "latest",
  } = query;

  const trimmed = keyword.trim().toLowerCase();

  const result = list.filter((m) => {
    if (type && m.type !== type) return false;
    if (region && region !== "全部" && m.region !== region) return false;
    if (year && year !== "全部" && String(m.year) !== year) return false;
    if (genre && genre !== "全部" && !m.genres.includes(genre)) return false;
    if (trimmed) {
      const haystack = [m.title, m.director, m.description, m.region, ...m.actors, ...m.genres]
        .join(" ")
        .toLowerCase();
      if (!haystack.includes(trimmed)) return false;
    }
    return true;
  });

  return sortMovies(result, sort);
}

export function sortMovies(list: Movie[], sort: SortKey): Movie[] {
  const copy = [...list];
  if (sort === "views") {
    return copy.sort((a, b) => b.views - a.views);
  }
  if (sort === "rating") {
    return copy.sort((a, b) => b.rating - a.rating);
  }
  return copy.sort((a, b) => b.year - a.year || b.views - a.views);
}

export function getMovieById(list: Movie[], id: string): Movie | undefined {
  return list.find((m) => m.id === id);
}

export function getRelatedMovies(list: Movie[], current: Movie, limit = 6): Movie[] {
  return list
    .filter((m) => m.id !== current.id)
    .map((m) => {
      const sharedGenres = m.genres.filter((g) => current.genres.includes(g)).length;
      const score = sharedGenres * 10 + (m.type === current.type ? 5 : 0) + m.rating / 10;
      return { m, score };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((item) => item.m);
}

export function paginate<T>(list: T[], page: number, pageSize: number) {
  const total = list.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;
  return {
    items: list.slice(start, start + pageSize),
    total,
    totalPages,
    page: safePage,
  };
}